'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'

// Types
type ResultType = 'artist' | 'category' | 'lot'
type ViewMode = 'grid' | 'list'

interface SearchResult {
  id: string
  type: ResultType
  title: string
  subtitle?: string
  href: string
  image?: string
  meta?: {
    trend?: number
    tier?: string
    price?: string
    movement?: string
    date?: string
  }
}

// Mock data
const allResults: SearchResult[] = [
  // Artists
  { id: 'a1', type: 'artist', title: 'Yayoi Kusama', subtitle: 'Japanese, b. 1929', href: '/app/artists/yayoi-kusama', meta: { trend: 18.4, tier: 'Blue-chip', movement: 'Pop Art' } },
  { id: 'a2', type: 'artist', title: 'Jean-Michel Basquiat', subtitle: 'American, 1960–1988', href: '/app/artists/jean-michel-basquiat', meta: { trend: 12.1, tier: 'Blue-chip', movement: 'Neo-Expressionism' } },
  { id: 'a3', type: 'artist', title: 'Gerhard Richter', subtitle: 'German, b. 1932', href: '/app/artists/gerhard-richter', meta: { trend: -2.3, tier: 'Blue-chip', movement: 'Conceptual Art' } },
  { id: 'a4', type: 'artist', title: 'Banksy', subtitle: 'British, b. 1974', href: '/app/artists/banksy', meta: { trend: 8.5, tier: 'Blue-chip', movement: 'Street Art' } },
  { id: 'a5', type: 'artist', title: 'Amoako Boafo', subtitle: 'Ghanaian, b. 1984', href: '/app/artists/amoako-boafo', meta: { trend: 42.1, tier: 'Mid-market', movement: 'African Contemporary' } },
  { id: 'a6', type: 'artist', title: 'Flora Yukhnovich', subtitle: 'British, b. 1990', href: '/app/artists/flora-yukhnovich', meta: { trend: 38.2, tier: 'Emerging', movement: 'Contemporary' } },
  { id: 'a7', type: 'artist', title: 'Jadé Fadojutimi', subtitle: 'British, b. 1993', href: '/app/artists/jade-fadojutimi', meta: { trend: 35.0, tier: 'Emerging', movement: 'Contemporary' } },
  { id: 'a8', type: 'artist', title: 'Christina Quarles', subtitle: 'American, b. 1985', href: '/app/artists/christina-quarles', meta: { trend: 28.5, tier: 'Mid-market', movement: 'Contemporary' } },
  // Categories
  { id: 'c1', type: 'category', title: 'Contemporary', subtitle: '$4.2B volume • 124K lots', href: '/app/market/contemporary', meta: { trend: 12.4 } },
  { id: 'c2', type: 'category', title: 'Impressionist & Modern', subtitle: '$3.8B volume • 89K lots', href: '/app/market/impressionist-modern', meta: { trend: 8.2 } },
  { id: 'c3', type: 'category', title: 'Post-War', subtitle: '$2.1B volume • 67K lots', href: '/app/market/post-war', meta: { trend: -3.1 } },
  { id: 'c4', type: 'category', title: 'Photography', subtitle: '$890M volume • 45K lots', href: '/app/market/photography', meta: { trend: 15.8 } },
  { id: 'c5', type: 'category', title: 'Street Art', subtitle: '$620M volume • 28K lots', href: '/app/market/street-art', meta: { trend: 22.4 } },
  // Lots
  { id: 'l1', type: 'lot', title: 'Infinity Nets (QRTWE)', subtitle: 'Yayoi Kusama', href: '/app/artists/yayoi-kusama', meta: { price: '$8.9M', date: 'Jan 2024' } },
  { id: 'l2', type: 'lot', title: 'Untitled (Devil)', subtitle: 'Jean-Michel Basquiat', href: '/app/artists/jean-michel-basquiat', meta: { price: '$42.4M', date: 'Feb 2024' } },
  { id: 'l3', type: 'lot', title: 'Girl with Balloon', subtitle: 'Banksy', href: '/app/artists/banksy', meta: { price: '$1.4M', date: 'Mar 2024' } },
]

const typeFilters: { id: ResultType | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'artist', label: 'Artists' },
  { id: 'category', label: 'Categories' },
  { id: 'lot', label: 'Lots' },
]

const tierFilters = ['All Tiers', 'Blue-chip', 'Mid-market', 'Emerging']
const movementFilters = ['All Movements', 'Contemporary', 'Pop Art', 'Neo-Expressionism', 'Street Art', 'African Contemporary', 'Conceptual Art']
const sortOptions = ['Relevance', 'Trending', 'A-Z', 'Z-A']

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<ResultType | 'all'>('all')
  const [tierFilter, setTierFilter] = useState('All Tiers')
  const [movementFilter, setMovementFilter] = useState('All Movements')
  const [sortBy, setSortBy] = useState('Relevance')
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // Filter and sort results
  const filteredResults = useMemo(() => {
    let results = allResults

    // Text search
    if (query.trim()) {
      const q = query.toLowerCase()
      results = results.filter(r =>
        r.title.toLowerCase().includes(q) ||
        r.subtitle?.toLowerCase().includes(q) ||
        r.meta?.movement?.toLowerCase().includes(q)
      )
    }

    // Type filter
    if (typeFilter !== 'all') {
      results = results.filter(r => r.type === typeFilter)
    }

    // Tier filter (artists only)
    if (tierFilter !== 'All Tiers') {
      results = results.filter(r => r.type !== 'artist' || r.meta?.tier === tierFilter)
    }

    // Movement filter
    if (movementFilter !== 'All Movements') {
      results = results.filter(r => r.meta?.movement === movementFilter)
    }

    // Sort
    if (sortBy === 'Trending') {
      results = [...results].sort((a, b) => (b.meta?.trend || 0) - (a.meta?.trend || 0))
    } else if (sortBy === 'A-Z') {
      results = [...results].sort((a, b) => a.title.localeCompare(b.title))
    } else if (sortBy === 'Z-A') {
      results = [...results].sort((a, b) => b.title.localeCompare(a.title))
    }

    return results
  }, [query, typeFilter, tierFilter, movementFilter, sortBy])

  const resultCounts = useMemo(() => ({
    all: allResults.length,
    artist: allResults.filter(r => r.type === 'artist').length,
    category: allResults.filter(r => r.type === 'category').length,
    lot: allResults.filter(r => r.type === 'lot').length,
  }), [])

  const clearFilters = () => {
    setTypeFilter('all')
    setTierFilter('All Tiers')
    setMovementFilter('All Movements')
    setSortBy('Relevance')
  }

  const hasActiveFilters = typeFilter !== 'all' || tierFilter !== 'All Tiers' || movementFilter !== 'All Movements'

  return (
    <div className="px-4 md:px-6 py-6">
      {/* Search Header */}
      <div className="mb-6">
        <div className="relative max-w-2xl">
          <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search artists, categories, lots, movements..."
            className="w-full pl-12 pr-4 py-4 rounded-xl border border-border bg-card text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-primary/10 transition-all text-lg"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-page text-text-tertiary transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Type Tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
        {typeFilters.map(filter => (
          <button
            key={filter.id}
            onClick={() => setTypeFilter(filter.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              typeFilter === filter.id
                ? 'bg-text-primary text-white'
                : 'bg-card border border-border text-text-secondary hover:border-border-hover'
            }`}
          >
            {filter.label}
            <span className={`ml-2 ${typeFilter === filter.id ? 'text-white/70' : 'text-text-tertiary'}`}>
              {resultCounts[filter.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Tier filter */}
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className={`px-4 py-2 rounded-lg text-sm border bg-card cursor-pointer transition-all ${
            tierFilter !== 'All Tiers' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
          }`}
        >
          {tierFilters.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Movement filter */}
        <select
          value={movementFilter}
          onChange={(e) => setMovementFilter(e.target.value)}
          className={`px-4 py-2 rounded-lg text-sm border bg-card cursor-pointer transition-all ${
            movementFilter !== 'All Movements' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
          }`}
        >
          {movementFilters.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
          >
            Clear filters
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 rounded-lg text-sm border border-border bg-card text-text-secondary cursor-pointer"
        >
          {sortOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* View toggle */}
        <div className="flex items-center border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-text-primary text-white' : 'bg-card text-text-tertiary hover:text-text-secondary'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-text-primary text-white' : 'bg-card text-text-tertiary hover:text-text-secondary'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4">
        <p className="text-sm text-text-tertiary">
          {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''}
          {query && <span> for "{query}"</span>}
        </p>
      </div>

      {/* Results */}
      {filteredResults.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-text-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <h3 className="font-medium text-lg mb-2">No results found</h3>
          <p className="text-text-secondary mb-4">
            {query ? `Nothing matches "${query}"` : 'Try adjusting your filters'}
          </p>
          <button
            onClick={() => { setQuery(''); clearFilters(); }}
            className="text-accent-primary hover:underline"
          >
            Clear search and filters
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="space-y-2">
          {filteredResults.map(result => (
            <Link
              key={result.id}
              href={result.href}
              className="card p-4 flex items-center gap-4 hover:border-border-hover transition-all group"
            >
              {/* Type icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                result.type === 'artist' ? 'bg-accent-primary/10 text-accent-primary' :
                result.type === 'category' ? 'bg-positive/10 text-positive' :
                'bg-accent-warm/10 text-accent-warm'
              }`}>
                {result.type === 'artist' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
                {result.type === 'category' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                )}
                {result.type === 'lot' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="font-medium group-hover:text-accent-primary transition-colors truncate">
                  {result.title}
                </p>
                <p className="text-sm text-text-tertiary truncate">{result.subtitle}</p>
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {result.meta?.tier && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    result.meta.tier === 'Blue-chip' ? 'bg-accent-primary/10 text-accent-primary' :
                    result.meta.tier === 'Mid-market' ? 'bg-accent-warm/10 text-accent-warm' :
                    'bg-positive/10 text-positive'
                  }`}>
                    {result.meta.tier}
                  </span>
                )}
                {result.meta?.trend !== undefined && (
                  <span className={`text-sm font-medium tabular-nums ${result.meta.trend >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {result.meta.trend >= 0 ? '+' : ''}{result.meta.trend}%
                  </span>
                )}
                {result.meta?.price && (
                  <span className="text-sm font-medium tabular-nums">{result.meta.price}</span>
                )}
                <svg className="w-5 h-5 text-text-tertiary group-hover:text-accent-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredResults.map(result => (
            <Link
              key={result.id}
              href={result.href}
              className="card p-4 hover:border-border-hover transition-all group"
            >
              {/* Type badge */}
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  result.type === 'artist' ? 'bg-accent-primary/10 text-accent-primary' :
                  result.type === 'category' ? 'bg-positive/10 text-positive' :
                  'bg-accent-warm/10 text-accent-warm'
                }`}>
                  {result.type}
                </span>
                {result.meta?.trend !== undefined && (
                  <span className={`text-xs font-medium tabular-nums ${result.meta.trend >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {result.meta.trend >= 0 ? '+' : ''}{result.meta.trend}%
                  </span>
                )}
              </div>

              {/* Title */}
              <p className="font-medium group-hover:text-accent-primary transition-colors mb-1 line-clamp-2">
                {result.title}
              </p>
              <p className="text-sm text-text-tertiary line-clamp-1">{result.subtitle}</p>

              {/* Meta */}
              {(result.meta?.tier || result.meta?.price) && (
                <div className="mt-3 pt-3 border-t border-border-subtle flex items-center justify-between">
                  {result.meta.tier && (
                    <span className="text-xs text-text-tertiary">{result.meta.tier}</span>
                  )}
                  {result.meta.price && (
                    <span className="text-sm font-medium">{result.meta.price}</span>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Recent Searches (when empty query) */}
      {!query && filteredResults.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border-subtle">
          <h3 className="text-sm font-medium text-text-secondary mb-4">Popular searches</h3>
          <div className="flex flex-wrap gap-2">
            {['Kusama', 'Basquiat', 'Street Art', 'Emerging Artists', 'Photography', 'Blue-chip'].map(term => (
              <button
                key={term}
                onClick={() => setQuery(term)}
                className="px-4 py-2 rounded-full text-sm bg-card border border-border text-text-secondary hover:border-border-hover transition-all"
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
